import { CommonModule } from '@angular/common';
import { Component, TemplateRef, inject } from '@angular/core';
import {
    UilabMatButton,
    UilabMatDialog,
    UilabMatDivider,
    UilabMatDialogModule,
} from '@uilab/ng-mat-components';
import { CodeExampleComponent } from 'src/app/shared/components/code-example/code-example.component';
import { CodeExampleHeaderComponent } from 'src/app/shared/components/code-example/code-example-header.component';
import { COMPONENT_EXAMPLES_CONSTANTS } from '../component-examples.constants';

interface DialogExample {
    readonly variant: 'basic';
    readonly label: string;
    readonly description: string;
    readonly htmlSnippet: string;
}

@Component({
    selector: 'avd-dialog-basic-preview',
    template: `<section class="avd-dialog-preview-trigger">
        <button
            uilabMatButton="filled"
            type="button"
            (click)="openDialog(dialogTpl)"
        >
            Open dialog
        </button>

        <ng-template #dialogTpl>
            <h2 uilab-mat-dialog-title>{{
                contents.dialog.title[caption].label
            }}</h2>
            <uilab-mat-divider></uilab-mat-divider>
            <uilab-mat-dialog-content>
                <p>{{ contents.dialog.content[caption].label }}</p>
                <p addMarginBottom>
                    {{ contents.dialog.extraContent[caption].label }}
                </p>
            </uilab-mat-dialog-content>
            <uilab-mat-divider></uilab-mat-divider>
            <uilab-mat-dialog-actions align="end">
                <button
                    uilabMatButton
                    [uilab-mat-dialog-close]="false"
                    [aria-label]="contents.dialog.cancelButton[caption].label"
                    >{{ contents.dialog.cancelButton[caption].label }}</button
                >
                <button
                    uilabMatButton="filled"
                    [uilab-mat-dialog-close]="true"
                    cdkFocusInitial
                    [aria-label]="contents.dialog.confirmButton[caption].label"
                >
                    {{ contents.dialog.confirmButton[caption].label }}
                </button>
            </uilab-mat-dialog-actions>
        </ng-template>
    </section>`,
    imports: [UilabMatButton, UilabMatDialogModule, UilabMatDivider],
})
export class DialogBasicPreviewComponent {
    private readonly dialog = inject(UilabMatDialog);
    readonly caption = 'basic';

    readonly contents = {
        dialog: {
            title: {
                basic: { label: 'Dialog title' },
            },
            content: {
                basic: {
                    label: 'Use dialogs to keep people focused on a short task or decision before returning them to the current page.',
                },
            },
            extraContent: {
                basic: {
                    label: 'Confirming closes this dialog and returns the result to the caller.',
                },
            },
            cancelButton: {
                basic: { label: 'Cancel' },
            },
            confirmButton: {
                basic: { label: 'Confirm' },
            },
        },
    } as const;

    openDialog(dialogTpl: TemplateRef<unknown>): void {
        this.dialog.open(dialogTpl, {
            height: '400px',
            width: '600px',
        });
    }
}

@Component({
    selector: 'avd-dialog-examples',
    templateUrl: './dialog-examples.component.html',
    styleUrls: ['./dialog-examples.component.scss'],
    imports: [
        CommonModule,
        CodeExampleHeaderComponent,
        CodeExampleComponent,
        DialogBasicPreviewComponent,
    ],
})
export class DialogExamplesComponent {
    readonly packageName = COMPONENT_EXAMPLES_CONSTANTS.PACKAGE_NAME;
    readonly getStartedLink = COMPONENT_EXAMPLES_CONSTANTS.GET_STARTED_LINK;
    readonly storybookLink = `${COMPONENT_EXAMPLES_CONSTANTS.STORYBOOK_BASE_URL}/?path=/story/components-dialog--default`;
    readonly repoLink = `${COMPONENT_EXAMPLES_CONSTANTS.AZURE_DEVOPS_BASE_URL}&path=/projects/components/src/lib/dialog`;

    readonly examples: readonly DialogExample[] = [
        {
            variant: 'basic',
            label: 'Basic Dialog',
            description:
                'Use a trigger button to open a dialog for short, interruptive tasks that need immediate acknowledgement or confirmation.',
            htmlSnippet: `<button uilabMatButton="filled" type="button" (click)="openDialog()">
    Open dialog
</button>

let dialogRef = dialog.open(dialogTpl, {
    height: '400px',
    width: '600px',
});

<ng-template #dialogTpl>
    <h2 uilab-mat-dialog-title>{{ contents.dialog.title[caption].label }}</h2>
    <uilab-mat-divider></uilab-mat-divider>
    <uilab-mat-dialog-content>
        <p>{{ contents.dialog.content[caption].label }}</p>
        <p addMarginBottom>
            {{ contents.dialog.extraContent[caption].label }}
        </p>
    </uilab-mat-dialog-content>
    <uilab-mat-divider></uilab-mat-divider>
    <uilab-mat-dialog-actions align="end">
        <button uilabMatButton [uilab-mat-dialog-close]="false">
            {{ contents.dialog.cancelButton[caption].label }}
        </button>
        <button
            uilabMatButton="filled"
            [uilab-mat-dialog-close]="true"
            cdkFocusInitial
        >
            {{ contents.dialog.confirmButton[caption].label }}
        </button>
    </uilab-mat-dialog-actions>
</ng-template>`,
        },
    ];
}
